import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from './Auth/AuthContext';
import { verifySocialConnect } from '../utils/socialApi';


// ============================================================================
// SocialAuthCallback — Popup window landing page after Instagram OAuth.
// Verifies the code, sends result back to opener window, then closes.
// ============================================================================

export default function SocialAuthCallback() {
    const { tokens, isLoading: authLoading } = useAuth();
    const [status, setStatus] = useState('verifying'); // 'verifying' | 'confirm' | 'success' | 'error'
    const [message, setMessage] = useState('Connecting your account...');
    const [conflict, setConflict] = useState(null); // { phoneNumber, displayName } when a WhatsApp takeover needs confirmation
    const [confirmBusy, setConfirmBusy] = useState(false);
    const verifiedRef = useRef(false);
    const ctxRef = useRef(null); // { platform, state, accessToken } kept for the confirm round-trip

    const finish = (res, platform) => {
        if (res.ok) {
            setStatus('success');
            setMessage(`Connected as @${res.data?.handle || 'your account'}`);
            if (window.opener) {
                window.opener.postMessage({
                    type: 'social-auth-result',
                    ok: true,
                    platform,
                    handle: res.data?.handle || '',
                }, window.location.origin);
            }
            setTimeout(() => window.close(), 1500);
        } else {
            setStatus('error');
            setMessage(res.error || 'Failed to verify connection');
            if (window.opener) {
                window.opener.postMessage({ type: 'social-auth-result', ok: false, error: res.error }, window.location.origin);
            }
            setTimeout(() => window.close(), 2500);
        }
    };

    useEffect(() => {
        // Guard against React StrictMode double-invoke
        if (verifiedRef.current) return;
        const params = new URLSearchParams(window.location.search);
        const platform = params.get('platform');
        const code = params.get('code');
        const state = params.get('state');
        const errorParam = params.get('error');
        const errorDesc = params.get('error_description');

        if (errorParam) {
            verifiedRef.current = true;
            setStatus('error');
            setMessage(errorDesc || errorParam || 'Authorization failed');
            // Notify opener
            if (window.opener) {
                window.opener.postMessage({ type: 'social-auth-result', ok: false, error: errorDesc || errorParam }, window.location.origin);
            }
            setTimeout(() => window.close(), 2000);
            return;
        }

        if (!platform || !code || !state) {
            verifiedRef.current = true;
            setStatus('error');
            setMessage('Missing authorization parameters');
            setTimeout(() => window.close(), 2000);
            return;
        }

        // Auth is still loading — wait for it (effect will re-run when tokens arrive)
        if (authLoading) return;

        if (!tokens?.accessToken) {
            setStatus('error');
            setMessage('Not authenticated. Please close and try again.');
            return;
        }

        // Lock BEFORE calling verify so StrictMode's second invocation aborts
        verifiedRef.current = true;
        ctxRef.current = { platform, state, accessToken: tokens.accessToken };

        verifySocialConnect({ platform, code, state, accessToken: tokens.accessToken })
            .then((res) => {
                if (res.ok && platform === 'whatsapp' && res.data?.conflict) {
                    setConflict({ phoneNumber: res.data.phoneNumber, displayName: res.data.displayName });
                    setStatus('confirm');
                    return;
                }
                finish(res, platform);
            })
            .catch(() => {
                setStatus('error');
                setMessage('Connection failed. Please try again.');
                if (window.opener) {
                    window.opener.postMessage({ type: 'social-auth-result', ok: false, error: 'Network error' }, window.location.origin);
                }
                setTimeout(() => window.close(), 2500);
            });
    }, [tokens?.accessToken, authLoading]);

    const handleCancelTakeover = () => {
        if (window.opener) {
            window.opener.postMessage({ type: 'social-auth-result', ok: false, error: 'cancelled' }, window.location.origin);
        }
        window.close();
    };

    const handleConfirmTakeover = () => {
        const ctx = ctxRef.current;
        if (!ctx || confirmBusy) return;
        setConfirmBusy(true);
        verifySocialConnect({ platform: ctx.platform, state: ctx.state, force: true, accessToken: ctx.accessToken })
            .then((res) => finish(res, ctx.platform))
            .catch(() => {
                setStatus('error');
                setMessage('Connection failed. Please try again.');
                if (window.opener) {
                    window.opener.postMessage({ type: 'social-auth-result', ok: false, error: 'Network error' }, window.location.origin);
                }
                setTimeout(() => window.close(), 2500);
            });
    };

    if (status === 'confirm') {
        return (
            <div style={{
                minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: '#fafaf9', fontFamily: 'system-ui, -apple-system, sans-serif',
            }}>
                <div style={{ textAlign: 'center', padding: 32, maxWidth: 420 }}>
                    <div style={{
                        width: 48, height: 48, margin: '0 auto 20px', borderRadius: '50%',
                        background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#92400e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12 9v4M12 17h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
                        </svg>
                    </div>
                    <div style={{ fontSize: 16, fontWeight: 600, color: '#1f2937', marginBottom: 8 }}>
                        Number already connected elsewhere
                    </div>
                    <div style={{ fontSize: 13, color: '#4b5563', lineHeight: 1.5, marginBottom: 20 }}>
                        <strong>{conflict?.phoneNumber || 'This number'}</strong> is already connected to another
                        Optimus Ink account. Continuing will disconnect it there — all of that account&apos;s
                        WhatsApp chats and conversations will be removed. (If they reconnect this number later,
                        their chats come back.) Continue?
                    </div>
                    <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                        <button
                            onClick={handleCancelTakeover}
                            disabled={confirmBusy}
                            style={{
                                padding: '9px 18px', borderRadius: 8, border: '1px solid #d1d5db',
                                background: '#fff', color: '#374151', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleConfirmTakeover}
                            disabled={confirmBusy}
                            style={{
                                padding: '9px 18px', borderRadius: 8, border: 'none',
                                background: '#dc2626', color: '#fff', fontSize: 13, fontWeight: 600,
                                cursor: confirmBusy ? 'default' : 'pointer', opacity: confirmBusy ? 0.7 : 1,
                            }}
                        >
                            {confirmBusy ? 'Connecting...' : 'Disconnect & connect here'}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={{
            minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: '#fafaf9', fontFamily: 'system-ui, -apple-system, sans-serif',
        }}>
            <div style={{ textAlign: 'center', padding: 40 }}>
                {status === 'verifying' && (
                    <>
                        <div style={{
                            width: 48, height: 48, margin: '0 auto 20px',
                            border: '3px solid #e5e7eb', borderTopColor: '#1f2937',
                            borderRadius: '50%', animation: 'spin 0.7s linear infinite',
                        }} />
                        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                    </>
                )}
                {status === 'success' && (
                    <div style={{
                        width: 48, height: 48, margin: '0 auto 20px', borderRadius: '50%',
                        background: '#d1fae5', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#065f46" strokeWidth="3" strokeLinecap="round"><polyline points="20 6 9 17 4 12" /></svg>
                    </div>
                )}
                {status === 'error' && (
                    <div style={{
                        width: 48, height: 48, margin: '0 auto 20px', borderRadius: '50%',
                        background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="3" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                    </div>
                )}
                <div style={{ fontSize: 16, fontWeight: 600, color: '#1f2937', marginBottom: 4 }}>
                    {status === 'verifying' ? 'Connecting...' : status === 'success' ? 'Connected!' : 'Error'}
                </div>
                <div style={{ fontSize: 13, color: '#6b7280' }}>{message}</div>
                {status !== 'verifying' && (
                    <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 12 }}>This window will close automatically.</div>
                )}
            </div>
        </div>
    );
}
